/**
 * In-app notifications API — typed client for job-queue-service's
 * notification feed (gateway prefix /api/v1/notifications, already routed —
 * see api-gateway/src/config.py).
 *
 * Backs BOTH notification families that ring the header bell: the
 * human-collaboration surface (docs/COLLABORATION_CONTRACT.md §3 — lead
 * assignment + invite/share/role events) AND the AI virtual-staff surface
 * (ai-orchestrator-service — an agent acted, needs the owner's arbitration,
 * breached its SLA, or delivered something; see NOTIFICATION_KIND_INFO
 * below for the full kind catalog). The exact response envelope isn't pinned
 * by the contract (only the delivery side — the worker — is specified), so
 * this client tolerates both a bare array and an `{items,total,unread_count}`
 * envelope, matching the "Growth surfaces" degrade-gracefully pattern already
 * used by lib/api/crm.ts (booking pages / forms / documents): a 404/501/503
 * here means "indisponible" (empty state), never a hard crash.
 */

import { api, ApiError } from '@/lib/api/client';

const BASE = '/api/v1/notifications';

function qs(params: Record<string, unknown> = {}): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

export interface AppNotification {
  id: string;
  tenant_id: string;
  user_id: string;
  /** Normalized event kind, e.g. `share_created`, `crm.lead_assigned`,
   *  `org.invite_accepted`. The backend field is `kind`; we mirror it onto
   *  `type` too so older call-sites keep working. */
  type: string;
  kind?: string;
  title: string;
  body: string | null;
  /** Deep-link target: for a share, `entity_type` is the scope
   *  (`group`|`contact`|`all`) and `entity_id` is the group/contact id. */
  entity_type?: string | null;
  entity_id?: string | null;
  read: boolean;
  read_at?: string | null;
  data?: Record<string, unknown>;
  created_at: string;
}

/** The backend (job-queue-service) returns `kind`/`entity_type`/`entity_id`;
 *  normalize each row so the UI has a stable `type` and the deep-link fields. */
function normalizeNotification(n: AppNotification): AppNotification {
  return { ...n, type: n.type ?? n.kind ?? '' };
}

/** UI grouping for the bell's segmented filter (Tout / IA / Collaboration) and
 *  for the NotificationPrefs panel's two sections. */
export type NotificationGroup = 'ai_staff' | 'collaboration';

export interface NotificationKindInfo {
  /** Emoji glyph — identity is never colour-alone. */
  icon: string;
  /** Short French label shown in the group filter + the prefs panel. */
  label: string;
  group: NotificationGroup;
  /** High-priority kinds (owner arbitration / SLA breach) get a visual
   *  standout in the bell — an accent border + a "Priorité" tag — instead of
   *  the plain unread treatment. */
  highPriority?: boolean;
}

/**
 * Every notification `kind` the platform currently emits, mapped to a
 * presentation. AI-staff kinds come from the ai-orchestrator-service virtual
 * staff (src/mas/capability_handlers.py `mas_persona_action`, src/hil/service.py
 * `staff_decision_needed`, src/staff/sla.py `staff_escalation`; `staff_delivery`
 * is the job-queue-service notifications API's reserved 4th AI-staff kind —
 * see services-python/job-queue-service/tests/test_notifications_api.py —
 * for a delivery-review producer). Collaboration kinds come from
 * job-queue-service's collaboration_notification_handler.py +
 * lead_assignment_handler.py (docs/COLLABORATION_CONTRACT.md §3).
 */
export const NOTIFICATION_KIND_INFO: Record<string, NotificationKindInfo> = {
  // --- AI virtual-staff (entity_type is usually `agent_task`) ---
  mas_persona_action: { icon: '🤖', label: 'Action d’agent IA', group: 'ai_staff' },
  staff_decision_needed: {
    icon: '⚠️',
    label: 'Décision requise',
    group: 'ai_staff',
    highPriority: true,
  },
  staff_escalation: {
    icon: '⏰',
    label: 'Escalade SLA',
    group: 'ai_staff',
    highPriority: true,
  },
  staff_delivery: { icon: '📦', label: 'Livrable prêt', group: 'ai_staff' },
  staff_question: { icon: '❓', label: 'Question d’agent', group: 'ai_staff' },
  // --- Human collaboration ---
  lead_assigned: { icon: '🎯', label: 'Prospect assigné', group: 'collaboration' },
  'crm.lead_assigned': { icon: '🎯', label: 'Prospect assigné', group: 'collaboration' },
  share_created: { icon: '🤝', label: 'Partage reçu', group: 'collaboration' },
  share_revoked: { icon: '🚫', label: 'Partage retiré', group: 'collaboration' },
  invite_accepted: { icon: '👋', label: 'Invitation acceptée', group: 'collaboration' },
  'org.invite_accepted': { icon: '👋', label: 'Invitation acceptée', group: 'collaboration' },
  role_changed: { icon: '🛡️', label: 'Rôle modifié', group: 'collaboration' },
  member_removed: { icon: '🚪', label: 'Membre retiré', group: 'collaboration' },
  note_mention: { icon: '💬', label: 'Mention dans une note', group: 'collaboration' },
};

export const DEFAULT_KIND_INFO: NotificationKindInfo = {
  icon: '🔔',
  label: 'Notification',
  group: 'collaboration',
};

/**
 * The canonical (current-backend) kinds, in display order — used to render
 * the NotificationPrefs panel's two sections. Deliberately narrower than
 * `Object.keys(NOTIFICATION_KIND_INFO)`: that catalog also carries legacy
 * dotted aliases (`crm.lead_assigned`, `org.invite_accepted` — kept only so
 * `kindInfo()` still classifies an older row correctly) which would otherwise
 * show up as confusing duplicate rows ("Prospect assigné" twice) in the prefs
 * panel.
 */
export const KNOWN_NOTIFICATION_KINDS: string[] = [
  'mas_persona_action',
  'staff_decision_needed',
  'staff_escalation',
  'staff_delivery',
  'lead_assigned',
  'share_created',
  'share_revoked',
  'invite_accepted',
  'role_changed',
  'member_removed',
  'note_mention',
];

/**
 * Kind -> presentation, with a substring fallback for a kind the catalog
 * above doesn't list yet (a future dotted/prefixed variant, or an older row) —
 * never a crash, never a bare "🔔 undefined". `mas_`/`staff_` prefixed kinds
 * default into the AI-staff group even before this catalog is extended.
 */
export function kindInfo(kindOrType: string | null | undefined): NotificationKindInfo {
  if (typeof kindOrType !== 'string' || !kindOrType) return DEFAULT_KIND_INFO;
  const direct = NOTIFICATION_KIND_INFO[kindOrType];
  if (direct) return direct;
  const k = kindOrType.toLowerCase();
  if (k.startsWith('mas_') || k.startsWith('staff_')) {
    return { icon: '🤖', label: 'Agent IA', group: 'ai_staff' };
  }
  if (k.includes('lead_assigned') || k.includes('assign')) return NOTIFICATION_KIND_INFO.lead_assigned;
  if (k.includes('invite')) return NOTIFICATION_KIND_INFO.invite_accepted;
  if (k.includes('share')) return NOTIFICATION_KIND_INFO.share_created;
  if (k.includes('role') || k.includes('member')) return NOTIFICATION_KIND_INFO.role_changed;
  if (k.includes('mention')) return NOTIFICATION_KIND_INFO.note_mention;
  return DEFAULT_KIND_INFO;
}

/** Which segmented-filter group a notification belongs to. */
export function notificationGroup(n: Pick<AppNotification, 'kind' | 'type'>): NotificationGroup {
  return kindInfo(n.kind ?? n.type).group;
}

export interface NotificationListParams {
  unread_only?: boolean;
  limit?: number;
  offset?: number;
}

export interface NotificationListResult {
  items: AppNotification[];
  total: number;
  /** Server-reported unread count when available; otherwise derive with
   *  `countUnread` over the loaded page (an honest under-estimate if the
   *  unread set is larger than the loaded page). */
  unread_count?: number;
}

/** Pure helper — counts unread rows in a loaded page. Exported so the "N"
 *  badge logic is unit-testable without any network/React harness. */
export function countUnread(items: AppNotification[]): number {
  return items.reduce((n, item) => (item.read ? n : n + 1), 0);
}

function isEnvelope(
  res: AppNotification[] | NotificationListResult,
): res is NotificationListResult {
  return !Array.isArray(res);
}

/** Tolerates a bare array OR an `{items,total,unread_count}` envelope; treats
 *  404/501/503 (feature not deployed) as an empty, non-throwing result. */
export async function listNotifications(
  params: NotificationListParams = {},
): Promise<NotificationListResult> {
  try {
    const res = await api.get<AppNotification[] | NotificationListResult>(
      `${BASE}${qs({ ...params })}`,
    );
    if (isEnvelope(res)) {
      const items = (res.items ?? []).map(normalizeNotification);
      return { items, total: res.total ?? items.length, unread_count: res.unread_count };
    }
    const items = res.map(normalizeNotification);
    return { items, total: items.length, unread_count: countUnread(items) };
  } catch (error) {
    if (error instanceof ApiError && [404, 501, 503].includes(error.status)) {
      return { items: [], total: 0, unread_count: 0 };
    }
    throw error;
  }
}

export function markNotificationRead(id: string): Promise<void> {
  return api.post<void>(`${BASE}/${encodeURIComponent(id)}/read`);
}

async function markEachNotificationRead(ids: string[]): Promise<string[]> {
  const failed: string[] = [];
  await Promise.all(
    ids.map(async (id) => {
      try {
        await markNotificationRead(id);
      } catch {
        failed.push(id);
      }
    }),
  );
  return failed;
}

/** "Tout marquer lu" — job-queue-service DOES expose a bulk endpoint
 *  (`POST /api/v1/notifications/read-all`, tenant+caller scoped), so mark
 *  everything in ONE round-trip instead of firing N per-id requests (which,
 *  on a large unread backlog, could itself trip the per-user rate limit).
 *  Falls back to per-id marking only if the bulk endpoint is unavailable on an
 *  older backend. Best-effort and never throws — returns the ids that failed
 *  (empty on success) so the caller can reconcile. */
export async function markAllNotificationsRead(ids: string[]): Promise<string[]> {
  try {
    await api.post<{ updated: number }>(`${BASE}/read-all`);
    return [];
  } catch (error) {
    // Endpoint absent on an older backend → degrade to per-id marking. Any other
    // failure also degrades (best-effort): the per-id pass surfaces real failures
    // as `failed` ids for the caller to reconcile, honouring the never-throw contract.
    void error;
    return markEachNotificationRead(ids);
  }
}
